import { useMemo, useState } from 'react'
import { Badge, Tag } from '../ui'
import { LabFlow } from './LabFlow'
import { BASELINE_CONDITIONS, getPathGenerators } from '../../data/layers'
import { useT } from '../../lib/i18n'
import type { LabDemoProps } from './types'

export function GoalDrivenDemo({ paradigm, lab, data }: LabDemoProps) {
  const t = useT()
  const demo = (data as typeof t.labDemos.goalDriven | undefined) ?? t.labDemos.goalDriven
  const wishes = demo.wishes
  const [selectedWishId, setSelectedWishId] = useState<string>(wishes[0]?.id ?? '')

  const selectedWish = wishes.find((w) => w.id === selectedWishId) ?? wishes[0]

  const generators = useMemo(() => getPathGenerators(t), [t])
  const path = useMemo(
    () => (selectedWish ? generators[selectedWish.goalId](BASELINE_CONDITIONS) : []),
    [generators, selectedWish],
  )

  return (
    <div className="space-y-5">
      <DemoHeader paradigm={paradigm} />

      <div className="flex flex-col gap-4">
        <UserActionPanel
          label={lab.userActionLabel}
          title={demo.wishesLabel}
          hint={demo.wishesHint}
        >
          <div className="grid sm:grid-cols-3 gap-2">
            {wishes.map((w) => {
              const active = w.id === selectedWishId
              return (
                <button
                  key={w.id}
                  type="button"
                  onClick={() => setSelectedWishId(w.id)}
                  aria-pressed={active}
                  className={`text-left px-3.5 py-3 rounded-[var(--radius-md)] transition-colors ${
                    active
                      ? 'bg-[color:var(--color-orion-amber)]/15 border border-[color:var(--color-orion-amber)]/60'
                      : 'border-faint hover:bg-[color:var(--color-orion-deep)]/40'
                  }`}
                >
                  <div
                    className={`mono text-[length:var(--text-micro)] uppercase tracking-widest mb-1 ${
                      active ? 'text-[color:var(--color-orion-amber)]' : 'text-[color:var(--color-orion-haze)]'
                    }`}
                  >
                    {w.speaker}
                  </div>
                  <p
                    className="text-[color:var(--color-orion-text)] leading-snug"
                    style={{ fontSize: 'var(--text-small)' }}
                  >
                    {w.quote}
                  </p>
                </button>
              )
            })}
          </div>
        </UserActionPanel>

        <OntologyResponsePanel label={lab.ontologyResponseLabel}>
          <LabFlow
            path={path}
            kindLabels={t.l3.kindLabels}
            pathKey={`goal-driven::${selectedWish?.goalId ?? 'none'}`}
            minHeight={360}
            maxHeight={520}
          />
        </OntologyResponsePanel>
      </div>

      <InsightCard title={demo.insightTitle} body={demo.insightBody} />
    </div>
  )
}

export function DemoHeader({ paradigm }: { paradigm: LabDemoProps['paradigm'] }) {
  return (
    <header className="flex items-start justify-between flex-wrap gap-3">
      <div className="min-w-0">
        <div className="flex items-baseline gap-3 mb-1">
          <span className="mono text-[28px] leading-none text-[color:var(--color-orion-amber)] font-light">
            {paradigm.number}
          </span>
          <h2
            className="text-[color:var(--color-orion-text)] font-medium leading-tight"
            style={{ fontSize: 'var(--text-h2)' }}
          >
            {paradigm.title}
          </h2>
        </div>
        <p
          className="text-[color:var(--color-orion-haze)] max-w-2xl"
          style={{ fontSize: 'var(--text-body)', lineHeight: 1.55 }}
        >
          {paradigm.tagline}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Badge tone="warn">{paradigm.triggerBadge}</Badge>
        <span className="text-[color:var(--color-orion-haze)] mono text-[length:var(--text-micro)]">→</span>
        <Badge tone="pass">{paradigm.outputBadge}</Badge>
      </div>
    </header>
  )
}

export function UserActionPanel({
  label,
  title,
  hint,
  children,
}: {
  label: string
  title?: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div
      className="surface-night border-faint rounded-[var(--radius-lg)] p-5"
      style={{ borderLeft: '2px solid color-mix(in oklch, var(--color-orion-amber) 60%, transparent)' }}
    >
      <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)] mb-3">
        {label}
      </div>
      {title && (
        <h3
          className="text-[color:var(--color-orion-text)] font-medium mb-1"
          style={{ fontSize: 'var(--text-body)' }}
        >
          {title}
        </h3>
      )}
      {hint && (
        <p
          className="text-[color:var(--color-orion-haze)] mb-3"
          style={{ fontSize: 'var(--text-small)', lineHeight: 1.55 }}
        >
          {hint}
        </p>
      )}
      {children}
    </div>
  )
}

export function OntologyResponsePanel({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div
      className="surface-night border-faint rounded-[var(--radius-lg)] p-5"
      style={{ borderLeft: '2px solid color-mix(in oklch, var(--color-orion-cyan) 60%, transparent)' }}
    >
      <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-cyan)] mb-3">
        {label}
      </div>
      {children}
    </div>
  )
}

export function InsightCard({ title, body }: { title: string; body: string }) {
  return (
    <div className="surface-night border-faint rounded-[var(--radius-lg)] p-5 flex items-start gap-4">
      <Tag kind="rule" className="shrink-0">
        {title}
      </Tag>
      <p
        className="text-[color:var(--color-orion-haze)]"
        style={{ fontSize: 'var(--text-body)', lineHeight: 1.55 }}
      >
        {body}
      </p>
    </div>
  )
}
